import React, { useEffect } from "react";
import { Form, useField } from "react-final-form";
import { useSelector } from "react-redux";
import { Box, Button, Typography } from "@mui/material";
import { useSnackbar } from "notistack";

import { LoadingButton } from "ui";

import { selectAllFolders, selectProjectsWithinAccount } from "fond/api";
import { useCreateOverlayMutation, useUpdateOverlayMutation } from "fond/api/overlaysSlice";
import { ProjectVersionField, TextField } from "fond/form/fields";
import { Project, Store, Version } from "fond/types";
import { CustomOverlayItem } from "fond/types/overlays";
import { isCollaborationProject, isMultiProject } from "fond/utils/project";
import { required } from "fond/utils/validation";
import { Modal } from "fond/widgets";

import VersionLayersPreview from "./VersionLayersPreview";

interface IFormData {
  Name: string;
  Project: Project | "";
  Version: Version;
}

interface IProps {
  /**
   * The overlay being edited. If undefined/empty, the modal is in create mode.
   */
  overlay: CustomOverlayItem | Partial<CustomOverlayItem>;
  /**
   * Callback function to handle the onClose of the modal
   */
  onClose(): void;
}

/**
 * NameField component is a wrapper around the TextField component that automatically sets
 * the value of the Name field to the ProjectName of the selected project when a project is selected.
 * This is only done when the Name field has not been touched or modified by the user.
 */
const NameField: React.FC<{ autoFill?: boolean }> = ({ autoFill = true }) => {
  const {
    input: { value: projectValue },
  } = useField("Project");

  const {
    meta: { touched, active },
    input: { onChange, value: nameValue },
  } = useField("Name");

  useEffect(() => {
    if (autoFill && !touched && !active) {
      onChange(projectValue?.ProjectName || "");
    }
  }, [autoFill, active, projectValue, touched, nameValue, onChange]);

  return <TextField name="Name" required label="Name" fullWidth validate={required} />;
};

const EditOverlayModal: React.FC<IProps> = ({ overlay, onClose }: IProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const versionId = useSelector((state: Store) => state.project.versionId);
  const project = useSelector((state: Store) => state.project.projects[state.project.projectId]?.project);
  const multiProjectId = project && isMultiProject(project) ? project.ID : undefined;
  const projects = useSelector(selectProjectsWithinAccount)
    .filter(isCollaborationProject)
    .filter(({ ID }) => ID !== project?.ID);
  const folders = useSelector(selectAllFolders);

  // Determine if we're in create or edit mode
  const isCreateMode = !overlay.ID;

  const [createOverlay, { isLoading: isCreating }] = useCreateOverlayMutation();
  const [updateOverlay, { isLoading: isUpdating }] = useUpdateOverlayMutation();
  const isSaving = isCreating || isUpdating;

  /**
   * On submit function called when the form is submitted and valid
   */
  const handleOnSubmit = async (values: IFormData) => {
    if (isCreateMode) {
      try {
        // Determine target type and ID
        const targetType = multiProjectId ? "MULTI_PROJECT" : "VERSION";
        const targetId = multiProjectId || versionId;

        await createOverlay({
          name: values.Name,
          sourceVersionId: values.Version.ID,
          targetType,
          targetId,
        }).unwrap();

        enqueueSnackbar(`Overlay "${values.Name}" created successfully.`, { variant: "success" });
        onClose();
      } catch (error) {
        enqueueSnackbar(`Failed to create overlay "${values.Name}".`, { variant: "error" });
      }
    } else {
      try {
        await updateOverlay({ overlayId: overlay.ID!, name: values.Name, versionId, multiProjectId }).unwrap();
        enqueueSnackbar(`Overlay "${values.Name}" updated successfully.`, { variant: "success" });
        onClose();
      } catch (error) {
        enqueueSnackbar(`Failed to update overlay "${values.Name}".`, { variant: "error" });
      }
    }
  };

  // Find the project and version from the overlay data (for edit mode)
  const initialProject = overlay.SourceVersion ? projects.find((p) => p.ID === overlay.SourceVersion?.Project?.ID) || "" : "";
  const initialVersion = (overlay.SourceVersion as any) || "";

  return (
    <Modal
      open
      header={isCreateMode ? "Add Overlay" : "Edit Overlay"}
      variant="primary"
      data-testid="edit-overlay-modal"
      onClose={onClose}
      content={
        <Box>
          <Form<IFormData>
            initialValues={{
              Name: overlay.Name || "",
              Project: initialProject,
              Version: initialVersion,
            }}
            onSubmit={handleOnSubmit}
            render={({ handleSubmit }) => (
              <form onSubmit={handleSubmit} id="edit-overlay-form">
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box mb={2}>
                      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                        Overlay source
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
                        Select the project and version that will be used as the source for this overlay.
                      </Typography>
                    </Box>
                    <Box>
                      <ProjectVersionField
                        projects={projects}
                        folders={folders}
                        projectValidator={required}
                        versionValidator={required}
                        disabled={!isCreateMode}
                      />
                    </Box>
                    <Box mb={2} />
                    <Box>
                      <NameField autoFill={isCreateMode} />
                    </Box>
                  </Box>
                  <Box>
                    <Box sx={{ mb: 1, fontWeight: 500 }}>Overlay Preview</Box>
                    <VersionLayersPreview />
                  </Box>
                </Box>
              </form>
            )}
          />
        </Box>
      }
      actions={
        <>
          <Button data-testid="edit-overlay-cancel-button" color="primary" onClick={onClose} sx={{ marginRight: 1 }}>
            Cancel
          </Button>
          <LoadingButton data-testid="edit-overlay-save-button" color="primary" loading={isSaving} type="submit" form="edit-overlay-form">
            {isCreateMode ? "Add" : "Update"}
          </LoadingButton>
        </>
      }
    />
  );
};

export default EditOverlayModal;
